import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Container, Grid, GridCol, GridRow, Heading, Text } from '@legalshield/adonis-ux-framework';

import { AllTaskValues, SubmitPayload, TaskKey } from './types';
import { ShellState, useDraftStorage } from './useDraftStorage';

export type TaskStepRenderArgs = {
  initialValues: AllTaskValues[TaskKey];
  allValues: AllTaskValues;
  onComplete: (values: AllTaskValues[TaskKey]) => void;
  onBack?: () => void;
  onCancelEdit?: () => void;
  submitLabel: string;
  isEditing: boolean;
};

export type TaskDefinition = {
  key: TaskKey;
  label: string;
  title: string;
  description?: string;
  render: (args: TaskStepRenderArgs) => React.ReactElement;
};

export type ConfirmationRenderArgs = {
  allValues: AllTaskValues;
  onEdit: (taskIndex: number) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
};

type ShellAction =
  | { type: 'SAVE_AND_ADVANCE'; key: TaskKey; value: AllTaskValues[TaskKey] }
  | { type: 'SAVE_AND_RETURN_TO_REVIEW'; key: TaskKey; value: AllTaskValues[TaskKey]; confirmationIndex: number }
  | { type: 'PREV' }
  | { type: 'JUMP'; index: number; editing: boolean }
  | { type: 'CANCEL_EDIT'; confirmationIndex: number };

const shellReducer = (state: ShellState, action: ShellAction): ShellState => {
  switch (action.type) {
    case 'SAVE_AND_ADVANCE': {
      const nextIndex = state.currentIndex + 1;
      const visited = new Set(state.visited);
      visited.add(nextIndex);
      return {
        ...state,
        values: { ...state.values, [action.key]: action.value } as AllTaskValues,
        currentIndex: nextIndex,
        editingFromConfirmation: false,
        visited,
      };
    }
    case 'SAVE_AND_RETURN_TO_REVIEW': {
      const visited = new Set(state.visited);
      visited.add(action.confirmationIndex);
      return {
        ...state,
        values: { ...state.values, [action.key]: action.value } as AllTaskValues,
        currentIndex: action.confirmationIndex,
        editingFromConfirmation: false,
        visited,
      };
    }
    case 'PREV':
      return state.currentIndex > 0
        ? { ...state, currentIndex: state.currentIndex - 1, editingFromConfirmation: false }
        : state;
    case 'JUMP': {
      const visited = new Set(state.visited);
      visited.add(action.index);
      return {
        ...state,
        currentIndex: action.index,
        editingFromConfirmation: action.editing,
        visited,
      };
    }
    case 'CANCEL_EDIT':
      return {
        ...state,
        currentIndex: action.confirmationIndex,
        editingFromConfirmation: false,
      };
    default:
      return state;
  }
};

type QuestionnaireShellProps = {
  tasks: ReadonlyArray<TaskDefinition>;
  confirmation: {
    label: string;
    title: string;
    render: (args: ConfirmationRenderArgs) => React.ReactElement;
  };
  onSubmit: (payload: SubmitPayload) => void | Promise<void>;
  storageKey?: string;
};

const ProgressBar = ({
  stepLabels,
  currentIndex,
  visited,
  onJump,
}: {
  stepLabels: string[];
  currentIndex: number;
  visited: Set<number>;
  onJump: (index: number) => void;
}) => (
  <div className="rnq-progress">
    <div className="rnq-progress__text">
      Step {currentIndex + 1} of {stepLabels.length}
    </div>
    <div className="rnq-progress__track">
      {stepLabels.map((label, idx) => {
        const isActive = idx === currentIndex;
        const isCompleted = visited.has(idx) && idx !== currentIndex;
        const isClickable = isCompleted;
        const cls = [
          'rnq-progress__bubble',
          isActive ? 'rnq-progress__bubble--active' : '',
          isCompleted ? 'rnq-progress__bubble--completed' : '',
          isClickable ? 'rnq-progress__bubble--clickable' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <button
            key={label + idx}
            type="button"
            className={cls}
            aria-label={label}
            disabled={!isClickable}
            onClick={() => isClickable && onJump(idx)}
          >
            <span>{idx + 1}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const QuestionnaireShell = ({ tasks, confirmation, onSubmit, storageKey }: QuestionnaireShellProps) => {
  const confirmationIndex = tasks.length;

  const { readDraft, persistDraft, clearDraft } = useDraftStorage(storageKey, tasks.length);

  const [state, dispatch] = useReducer(
    shellReducer,
    undefined,
    (): ShellState =>
      readDraft() ?? {
        currentIndex: 0,
        editingFromConfirmation: false,
        values: {},
        visited: new Set<number>([0]),
      },
  );

  useEffect(() => {
    persistDraft(state);
  }, [persistDraft, state]);

  const [submitting, setSubmitting] = useState(false);

  const stepLabels = useMemo(() => [...tasks.map((t) => t.label), confirmation.label], [tasks, confirmation.label]);

  const handleAdvance = useCallback(
    (key: TaskKey, value: AllTaskValues[TaskKey]) => {
      if (state.editingFromConfirmation) {
        dispatch({ type: 'SAVE_AND_RETURN_TO_REVIEW', key, value, confirmationIndex });
      } else {
        dispatch({ type: 'SAVE_AND_ADVANCE', key, value });
      }
    },
    [state.editingFromConfirmation, confirmationIndex],
  );

  const handlePrev = useCallback(() => dispatch({ type: 'PREV' }), []);

  const handleJump = useCallback((index: number) => dispatch({ type: 'JUMP', index, editing: false }), []);

  const handleEditFromConfirmation = useCallback(
    (taskIndex: number) => dispatch({ type: 'JUMP', index: taskIndex, editing: true }),
    [],
  );

  const handleCancelEdit = useCallback(() => dispatch({ type: 'CANCEL_EDIT', confirmationIndex }), [confirmationIndex]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      await onSubmit(state.values as SubmitPayload);
      clearDraft();
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, state.values, clearDraft]);

  const atConfirmation = state.currentIndex === confirmationIndex;
  const currentTask = !atConfirmation ? tasks[state.currentIndex] : null;

  const submitLabel = state.editingFromConfirmation ? 'Save & Return to Review' : 'Continue';

  return (
    <Container classNames={['rnq-shell']}>
      <Container classNames={['rnq-shell__header']}>
        <Heading as="T28" text="Referral Network Application" />
      </Container>
      <ProgressBar
        stepLabels={stepLabels}
        currentIndex={state.currentIndex}
        visited={state.visited}
        onJump={handleJump}
      />

      <Grid classNames={['rnq-shell__body', 'mt-4']}>
        <GridRow>
          <GridCol>
            {atConfirmation ? (
              <>
                <Heading as="T28" text={confirmation.title} classNames={['mb-3']} />
                {confirmation.render({
                  allValues: state.values,
                  onEdit: handleEditFromConfirmation,
                  onBack: handlePrev,
                  onSubmit: handleSubmit,
                  submitting,
                })}
              </>
            ) : currentTask ? (
              <div className="rnq-task-host">
                <Heading as="T28" text={currentTask.title} classNames={['mb-2']} />
                {currentTask.description ? (
                  <Text classNames={['rnq-task-host__description', 'mb-4']} text={currentTask.description} />
                ) : (
                  <></>
                )}
                {currentTask.render({
                  initialValues: state.values[currentTask.key] as AllTaskValues[TaskKey],
                  allValues: state.values,
                  onComplete: (value) => handleAdvance(currentTask.key, value),
                  onBack: state.currentIndex > 0 && !state.editingFromConfirmation ? handlePrev : undefined,
                  onCancelEdit: state.editingFromConfirmation ? handleCancelEdit : undefined,
                  submitLabel,
                  isEditing: state.editingFromConfirmation,
                })}
              </div>
            ) : (
              <></>
            )}
          </GridCol>
        </GridRow>
      </Grid>
    </Container>
  );
};

export default QuestionnaireShell;
